import React, { useState, useRef, useMemo, useEffect } from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { useNavigationType } from "react-router-dom";
import burger from "../../assets/burger.png";
import { useTranslation } from "react-i18next";
import defaultToy from "./../../assets/defaultToy.png";
import defaultAvatar from "./../../assets/cardPhoto.png";
import { selectIsAuth } from "../../redux/slices/auth";
import { getPagination, DOTS } from "./get-pagination";
import CardPreview from "../Card/card-preview";
import LoaderCircle from "../../ui-kit/loader-circle/loader-circle";
import {
  cardsSelect,
  fetchCards,
  saveScrollPosition,
  setCurrentPage,
  setPageSize,
  setFilterCathegory,
  setFilterSize,
  setFilterPrice,
  setFilterStatus,
} from "../../redux/slices/cards";
import Filters from "../../ui-kit/filters/filters";

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: fit-content;
  overflow: hidden;
  padding-bottom: 5rem;

  @media (max-width: 577px) {
    padding: 0 15px;
  }
`;

const MarketPart = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 23% 23% 23% 23%;
  justify-content: space-between;
  grid-gap: 30px;

  @media (max-width: 577px) {
    margin: 0;
    padding-top: 26px;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 15px;
    grid-template-rows: 269px;
    grid-auto-rows: 269px;

    & > a {
      overflow: hidden;
    }
  }
`;

const SectionMenu = styled.div`
  margin: 0 auto;
  border-top: 1px solid rgba(145, 114, 114, 0.2);
  border-bottom: 1px solid rgba(145, 114, 114, 0.2);
  height: 70px;

  width: 100%;
`;

const TextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 16px 0;
  width: 100%;
  position: relative;
`;

const Text = styled.ul`
  width: 280px;
  list-style: none;
  display: flex;
  justify-content: space-between;
  margin-top: -1rem;
  cursor: pointer;
  transition: 0.1s linear;

  .true {
    border-bottom: 3px solid rgba(254, 97, 3, 0.3);
    transform: scale(1.1);
  }

  .false {
    opacity: 0.5;
  }

  .false:hover {
    border-bottom: 3px solid rgba(254, 97, 3, 0.3);
    transform: scale(1.1);
    opacity: 0.8;
  }

  @media (max-width: 577px) {
    width: 200px;
    margin-left: 0;
  }
`;

const ItemMenu = styled.li`
  font-family: "Palatino";
  font-size: 28px;
  opacity: 0.75;
  transition: 0.5s ease;

  display: block;
  padding-top: 16px;
  height: fit-content;
  width: fit-content;

  @media (max-width: 577px) {
    font-size: 22px;
  }
`;

const Filter = styled.div`
  position: absolute;
  display: flex;
  right: 0;
  margin-top: 5px;
  font-family: "Montserrat";
  font-weight: 300;
  font-size: 14px;
  cursor: pointer;

  span {
    margin-top: 3px;
    margin-right: 10px;
  }

  img {
    opacity: 0.5;
    transform: scale(0.8);
  }

  @media (max-width: 577px) {
    span {
      display: none;
    }
  }
`;

const PaginationBtn = styled.button`
  width: 30px;
  height: 30px;
  color: #808183;
  border-bottom: 1px solid rgba(171, 154, 144, 0.3);
  border-top: 1px solid rgba(171, 154, 144, 0.3);
  border-right: 1px solid rgba(171, 154, 144, 0.3);
  border-left: none;
  cursor: pointer;
  background: transparent;
  opacity: 0.9;
  font-family: "Montserrat", sans-serif;
  display: block;
`;

const PaginationBtnFirst = styled.button`
  display: block;
  width: 30px;
  height: 30px;
  color: #808183;
  border: 1px solid rgba(145, 114, 114, 0.2);
  cursor: pointer;
  background: transparent;
  border-radius: 10px 0 0 10px;
  opacity: 0.9;
`;

const PaginationBtnActive = styled.button`
  display: block;
  width: 30px;
  height: 30px;
  color: #ffffff;
  border: 1px solid rgba(145, 114, 114, 0.2);
  cursor: pointer;
  background: #fe6103;
  opacity: 0.35;
  font-family: "Montserrat", sans-serif;
`;

const PaginationBtnLast = styled.button`
  display: block;
  width: 30px;
  height: 30px;
  color: #808183;
  border-right: 1px solid rgba(145, 114, 114, 0.2);
  border-top: 1px solid rgba(145, 114, 114, 0.2);
  border-bottom: 1px solid rgba(145, 114, 114, 0.2);
  border-left: none;
  cursor: pointer;
  background: transparent;
  border-radius: 0 10px 10px 0;
  opacity: 0.9;
`;

const PaginationWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
`;

const FiltersWrapper = styled.div`
  margin-top: 20px;
`;

const PAGE_SIZE = 44;

const Market = (e) => {
  const [isShown, setIsShown] = React.useState(false);
  const [isInitialFetched, setIsInitialFetched] = useState(false);
  const isAuth = useSelector(selectIsAuth);
  const dispatch = useDispatch();
  const cardsState = useSelector(cardsSelect);
  const filter = cardsState.filter;

  const sectionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const navType = useNavigationType();

  const updateFilterValue = (filter) => {
    dispatch(setFilterStatus(filter.status));
    dispatch(setFilterSize(filter.size));
    dispatch(setFilterPrice(filter.price));
    dispatch(setCurrentPage(1));
  };

  const pagination = useMemo(
    () =>
      getPagination({
        totalPageCount: cardsState.pages,
        currentPage: cardsState.currentPage,
      }),
    [cardsState.currentPage, cardsState.pages]
  );

  const { t } = useTranslation();

  const changePage = async (nextPage) => {
    setLoading(true);
    dispatch(setCurrentPage(nextPage));
    await dispatch(fetchCards({}));
    setLoading(false);

    sectionRef.current &&
      window.scrollTo({ top: sectionRef.current.offsetTop });
  };

  const onCategoryChange = async (nextCathegory) => {
    setLoading(true);
    dispatch(setFilterCathegory(nextCathegory));
    dispatch(setCurrentPage(1));
    await dispatch(fetchCards({}));
    setLoading(false);
  };

  React.useEffect(() => {
    dispatch(setPageSize(PAGE_SIZE));
    if (!cardsState.items.length) {
      setLoading(true);
    }
    dispatch(fetchCards({})).then(() => {
      setIsInitialFetched(true);
      setLoading(false);
    });
  }, []);

  const filteredCards = (filter) => {
    setLoading(true);
    updateFilterValue(filter);
    dispatch(fetchCards({}));
    setLoading(false);
  };

  const savePosition = () => {
    dispatch(saveScrollPosition(window.scrollY));
  };

  const handleScrollPosition = () => {
    const scrollPosition = cardsState.scrollPosition;

    if (scrollPosition && navType === "POP") {
      window.scrollTo({ top: scrollPosition, behavior: "auto" });
    }
    dispatch(saveScrollPosition(null));
  };

  useEffect(() => {
    isInitialFetched && handleScrollPosition();
  }, [isInitialFetched]);

  return (
    <Section ref={sectionRef}>
      <SectionMenu>
        <TextContainer>
          <Text>
            <ItemMenu
              className={cardsState.filter.cathegory === "teddy"}
              onClick={() => {
                onCategoryChange("teddy");
              }}
            >
              {t("teddy")}
            </ItemMenu>
            <ItemMenu
              className={cardsState.filter.cathegory === "doll"}
              onClick={() => {
                onCategoryChange("doll");
              }}
            >
              {t("dolls")}
            </ItemMenu>
          </Text>
          <Filter onClick={() =>{setIsShown(!isShown)}}>
            <span>{t('filter')}</span>
            <img src={burger} alt="burger" />
          </Filter>
        </TextContainer>
      </SectionMenu>
      {isShown && 
        <FiltersWrapper>
            <Filters currentFIlter={filter} onSubmit={filteredCards} />
        </FiltersWrapper> 
      }
      {loading ? (
        <div style={{ marginTop: 400 }}>
          <LoaderCircle />
        </div>
      ) : (
        <MarketPart>
          {cardsState.items?.map((obj) => {
            return (
              <div key={obj._id} onClick={savePosition}>
                <CardPreview
                  key={obj._id}
                  altWork={obj.name}
                  imageWork={
                    obj.imgUrl
                      ? `https://toobears.com/api/${obj.imgUrl[0]}`
                      : defaultToy
                  }
                  avatar={
                    `https://toobears.com/api/${obj.author.avatarUrl}` ||
                    defaultAvatar
                  }
                  shopName={
                    obj.author.shopname ||
                    `${obj.author.username} ${obj.author.surname}`
                  }
                  nameWork={obj.name}
                  price={obj.price + " " + obj.currency}
                  size={obj.size + "cm"}
                  likeActive={obj.like.includes(isAuth && isAuth._id)}
                  status={obj.status}
                  link={`/card/${obj._id}`}
                />
              </div>
            );
          })}
        </MarketPart>
      )}
      <PaginationWrapper>
        {pagination.length > 0 && (
          <>
            <PaginationBtnFirst
              onClick={() => changePage(1)}
            >{`<<`}</PaginationBtnFirst>
            {pagination.map((value) =>
              value === cardsState.currentPage ? (
                <PaginationBtnActive key={value} disabled>
                  {value}
                </PaginationBtnActive>
              ) : value === DOTS ? (
                <PaginationBtn key={value}>{DOTS}</PaginationBtn>
              ) : (
                <PaginationBtn key={value} onClick={() => changePage(value)}>
                  {value}
                </PaginationBtn>
              )
            )}
            <PaginationBtnLast
              onClick={() => changePage(pagination[pagination.length - 1])}
            >{`>>`}</PaginationBtnLast>
          </>
        )}
      </PaginationWrapper>
    </Section>
  );
};

export default Market;
